import {all, call, cancelled, fork, put, race, select, take, takeLatest} from 'redux-saga/effects'
import {get, isEmpty} from 'lodash'
import {resourceModesTableKey, resourceTableKey} from 'reducers/pricingCalculator'
import {createPivotTableWatcher} from 'sagas/pivotTable'
import {sortModes} from 'helpers/resources'
import {deSelectAllRows, requestData, updateData} from 'actions/pivotTable'
import {getAllSelectedRows} from 'selectors/pivotTable'
import {getPricingCalculatorAccessToken} from 'selectors/auth'
import {getPackTypes} from 'selectors/priceLists'
import {
  getFieldMappings,
  getFormDefinition,
  getLastExecution,
  getPartPricing,
  getResourceModes,
  getSelectedResourceType,
  isIncludingDormantResources,
} from 'selectors/pricingCalculator'
import types from 'actions/types'
import {generalError} from 'actions/index'
import {snackbarMessage} from 'actions/snackbar'
import {iconNames} from 'components/icon'
import {createPricingCalculatorAccessTokenFailure, createPricingCalculatorAccessTokenSuccess} from 'actions/auth'
import {hidePricingCalculatorDrawer, pricingCalculatorDrawerPartLoaded} from 'actions/quoting'
import {
  addResourceFailure,
  addResourceSuccess,
  fetchCalculatorFailure,
  fetchCalculatorFormFailure,
  fetchCalculatorFormSuccess,
  fetchCalculatorSuccess,
  fetchResourcesFormFailure,
  fetchResourcesFormSuccess,
  loadAllPricingCalculatorsFailure,
  loadAllPricingCalculatorsSuccess,
  loadResourcesFailure,
  loadResourcesSuccess,
  loadPricingCalculatorFormFailure,
  loadPricingCalculatorFormSuccess,
  loadPricingCalculatorMasterFailure,
  loadPricingCalculatorMasterSuccess,
  runPricingCalculationFailure,
  runPricingCalculationSuccess,
  sendPricingCalculatorToSupportFailure,
  sendPricingCalculatorToSupportSuccess,
  setFieldMappings,
  setLastPricingCalculatorExecution,
  setPartPricing,
  updatePricingCalculatorMasterFailure,
  updatePricingCalculatorMasterSuccess,
  updateResourceFailure,
  updateResourceSuccess,
} from 'actions/pricingCalculator'
import {isExpiringSoon} from 'helpers/jwt'
import {OrderItemType} from 'helpers/orders'
import {
  CALCULATOR_NAME,
  CALCULATOR_VERSION,
  getPartCalculator,
  getPricingPack,
  INPUT_LEVELS,
  isCalculatorBearingPivotKey,
  mapDynamicFormDataToRequest, TOKEN_SCOPE,
} from 'helpers/pricingCalculator'

const calculatorFields = {
  default: true,
  inputs: {
    default: true,
    parts: {
      default: true,
      packs: true,
    },
  },
}

const priceListPartFields = {
  default: true,
  taxCode: true,
  pricingCalculator: calculatorFields,
}

export default function getPricingCalculatorSaga(api) {

  function* getOrRefreshToken() {
    let token = yield select(getPricingCalculatorAccessToken)

    if (token && !isExpiringSoon(token)) {
      return token
    }

    const response = yield call(api.pricingCalculatorAccessToken.create, {scope: TOKEN_SCOPE})
    yield api.handleResponse(
      response,
      function* onOk(key, data) {
        token = data.result
        yield put(createPricingCalculatorAccessTokenSuccess(token))
      },
      function* onFail(key, message, code) {
        token = null
        yield put(createPricingCalculatorAccessTokenFailure(message, code))
      })

    return token
  }

  function* fetchForm(token, calculatorName = CALCULATOR_NAME, calculatorVersion = CALCULATOR_VERSION) {
    try {
      if (!token) return

      const response = yield call(api.pricingCalculator.getForm, token, calculatorName, calculatorVersion)

      if (response.ok) {
        yield put(fetchCalculatorFormSuccess(response.data))
      } else {
        const errorMessage = get(response, 'data.error.message', 'Failed to fetch calculator form')
        yield put(fetchCalculatorFormFailure(errorMessage))
      }
    } catch (e) {
      yield put(fetchCalculatorFormFailure(e.message))
      console.error(e)
    }
  }

  function* fetchCalculator(calculatorName = CALCULATOR_NAME, calculatorVersion = CALCULATOR_VERSION) {
    try {
      // Existing items need this too, as their master inputs may have changed since the quote loaded.
      const response = yield call(
        api.pricingCalculator.getCalculatorInstance,
        null,
        calculatorName,
        calculatorVersion,
        calculatorFields
      )
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(fetchCalculatorSuccess(data.result[0]))
        },
        function* onFail(key, message, code) {
          yield put(fetchCalculatorFailure(message, code))
          console.error(message)
        }
      )

    } catch (e) {
      yield put(fetchCalculatorFailure(e.message))
      console.error(e)
    }
  }

  function* loadPriceListPart(id) {
    let part = null

    try {
      const params = {
        id,
        search: null,
        containingCategory: null,
        category: null,
        priceList: null,
      }

      const response = yield call(api.parts.get, params, priceListPartFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          part = get(data, 'result[0]', null)
        },
        function* onFail(key, message) {
          yield put(snackbarMessage(message, iconNames.error))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(snackbarMessage(e.message, iconNames.error))
    }

    if (!part) {
      yield put(hidePricingCalculatorDrawer())
    }
    return part
  }

  function* onDrawerOpen({priceListPart}) {
    let part = null

    if (priceListPart) {
      part = yield call(loadPriceListPart, priceListPart.id)
      if (!part) return
    }

    const {calculatorName, calculatorVersion} = getPartCalculator(part)
    yield call(fetchCalculator, calculatorName, calculatorVersion)
    yield fork(loadResources)
    const token = yield call(getOrRefreshToken)
    yield call(fetchForm, token, calculatorName, calculatorVersion)

    // Only fill the drawer from the part once its own calculator and form have arrived
    if (part) {
      yield put(pricingCalculatorDrawerPartLoaded(part))
    }
  }

  function* loadPartPricingCalculatorForm({calculatorName, calculatorVersion}) {
    yield fork(loadResources)
    const token = yield call(getOrRefreshToken)
    if (!token) {
      yield put(fetchCalculatorFormFailure('Calculator access token not available'))
      return
    }
    yield call(fetchForm, token, calculatorName, calculatorVersion)
  }

  function* fetchResourcesForm() {
    try {
      const token = yield call(getOrRefreshToken)
      if (!token) {
        yield put(fetchResourcesFormFailure('Calculator access token not available'))
        return
      }

      const response = yield call(api.pricingCalculator.getResourcesForm, token)
      if (response.ok) {
        yield put(fetchResourcesFormSuccess(response.data))
      } else {
        const errorMessage = get(response, 'data.error.message', 'Failed to fetch resources form')
        yield put(fetchResourcesFormFailure(errorMessage))
      }
    } catch (e) {
      yield put(fetchResourcesFormFailure(e.message))
    }
  }

  const resourceLoadParams = (typeName) => (state) => ({
    type: [typeName],
    incDormant: isIncludingDormantResources(state, typeName),
  })

  const forkedResourceTypes = new Set()

  function* reloadSelectedResources() {
    const typeName = yield select((state) => get(getSelectedResourceType(state), 'name'))
    if (typeName) yield put(requestData(resourceTableKey(typeName)))
  }

  function* forkResourceWatchers({resourceTypes}) {
    const names = (resourceTypes || [])
      .map((resourceType) => get(resourceType, 'name'))
      .filter((typeName) => typeName && !forkedResourceTypes.has(typeName))

    for (const typeName of names) {
      forkedResourceTypes.add(typeName)
      yield fork(createPivotTableWatcher(
        api.resource,
        resourceTableKey(typeName),
        [],
        resourceLoadParams(typeName)
      ))
    }

    yield call(reloadSelectedResources)
  }

  // The resources scene refetches its form every time it mounts, so this cannot be wired up
  // with takeLatest: restarting the handler would cancel the pivot table watchers forked by
  // the previous run, and forkedResourceTypes would then stop them from being forked again,
  // leaving sorting, paging and searching dead until the page was reloaded. The watchers are
  // forked from this task instead, which lives for as long as the saga does.
  function* watchResourceTypes() {
    while (true) {
      const action = yield take(types.PRICING_CALCULATOR_RESOURCES_FORM_FETCH_SUCCESS)
      yield call(forkResourceWatchers, action)
    }
  }

  function* setResourcesDormant({resourceType, dormant}) {
    try {
      const key = resourceTableKey(resourceType)
      const selected = yield select((state) => getAllSelectedRows(state, key))
      const changing = selected.filter((resource) => get(resource, 'dormant', false) !== dormant)

      if (isEmpty(changing)) {
        yield put(deSelectAllRows(key))
        return
      }

      const responses = yield all(changing.map((resource) =>
        call(api.resource.update, {resource: {...resource, dormant}})))

      const failures = responses.filter((response) =>
        !response.ok || get(response, 'data.data[0].success') !== true)
      const noun = changing.length === 1 ? 'resource' : 'resources'

      if (isEmpty(failures)) {
        yield put(snackbarMessage(
          `${changing.length} ${noun} ${dormant ? 'made dormant' : 'restored'}`,
          iconNames.tick
        ))
      } else {
        const message = get(failures[0], 'data.data[0].message', `Failed to update ${noun}`)
        yield put(snackbarMessage(message, iconNames.error))
      }

      yield put(deSelectAllRows(key))
      yield put(requestData(key))
    } catch (e) {
      yield put(snackbarMessage(e.message, iconNames.error))
    }
  }

  function* loadResources() {
    try {
      const response = yield call(api.resource.get, {incDormant: true})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(loadResourcesSuccess(get(data, 'result', [])))
        },
        function* onFail(key, message) {
          yield put(loadResourcesFailure(message))
          yield put(snackbarMessage(message, iconNames.error))
        })
    } catch (e) {
      yield put(loadResourcesFailure(e.message))
    }
  }

  function* addResource({resource}) {
    try {
      const response = yield call(api.resource.add, {resource})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(addResourceSuccess(get(data, 'result')))
          yield put(snackbarMessage('Resource added', iconNames.tick))
        },
        function* onFail(key, message) {
          yield put(addResourceFailure(message))
          yield put(snackbarMessage(message, iconNames.error))
        })
    } catch (e) {
      yield put(addResourceFailure(e.message))
      yield put(snackbarMessage(e.message, iconNames.error))
    }
  }

  function* updateResource({resource}) {
    try {
      const response = yield call(api.resource.update, {resource})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(updateResourceSuccess(get(data, 'result')))
          yield put(snackbarMessage('Resource updated', iconNames.tick))
        },
        function* onFail(key, message) {
          yield put(updateResourceFailure(message))
          yield put(snackbarMessage(message, iconNames.error))
        })
    } catch (e) {
      yield put(updateResourceFailure(e.message))
      yield put(snackbarMessage(e.message, iconNames.error))
    }
  }

  function* sortModesOnAdd({key}) {
    if (key !== resourceModesTableKey) return

    const modes = yield select(getResourceModes)
    yield put(updateData(resourceModesTableKey, sortModes(modes)))
  }

  function* fetchPartPricing(part, order) {
    const params = {
      partId: part.id,
      packId: get(getPricingPack(part), 'id', null),
      quantity: 1,
      currencyRate: get(order, 'currencyRate', 1),
      companyId: get(order, 'companyId', null),
      sizeX: get(part, 'sizeX', 0),
      sizeY: get(part, 'sizeY', 0),
      unitPriceSet: false,
      unitCostSet: false,
    }

    const response = yield call([api.parts, api.parts.getPrice], params)

    const pricing = {unitCost: 0, unitPrice: 0}

    yield api.handleResponse(
      response,
      function* (key, data) {
        if (data.result && data.result.length > 0) {
          const price = data.result[0]
          pricing.unitCost = get(price, 'unitCost', 0)
          pricing.unitPrice = get(price, 'unitPrice', 0)
          yield put(setPartPricing(part.id, pricing))
        }
      },
      function* (key, message) {
        console.error('Failed to get part price for calculator', message)
      }
    )

    return pricing
  }

  function* getPricingForPart(part, order) {
    if (!part || !part.id) {
      return {unitCost: 0, unitPrice: 0}
    }
    const partPricing = yield select(getPartPricing)

    return partPricing[part.id] || (yield call(fetchPartPricing, part, order))
  }

  function* runCalculation({formData, order}) {
    try {
      const token = yield call(getOrRefreshToken)

      if (!token) {
        yield put(runPricingCalculationFailure('Calculator access token not available'))
        return
      }

      const formDefinition = yield select(getFormDefinition)
      const dynamicInputs = formData.dynamicInputs || {}
      const lineItemDetails = formData.lineItemDetails || {}

      const inputsWithParts = formDefinition.inputs
        .filter(input => input && input.type === 'part')
        .map(input => ({input, part: get(dynamicInputs, `${input.name}.part`)}))
        .filter(({part}) => part && part.id)

      const pricingResults = yield all(
        inputsWithParts.map(({part}) => call(getPricingForPart, part, order))
      )

      const pricingByInputName = {}
      inputsWithParts.forEach(({input}, i) => {
        pricingByInputName[input.name] = pricingResults[i]
      })

      const request = mapDynamicFormDataToRequest(
        dynamicInputs,
        formDefinition,
        pricingByInputName,
        lineItemDetails,
        yield select(getPackTypes),
        get(order, 'currencyRate', 1)
      )
      yield put(setLastPricingCalculatorExecution(formDefinition.calculator, formDefinition.version, request))

      const response = yield call(
        api.pricingCalculator.run,
        token,
        formDefinition.calculator,
        formDefinition.version,
        request
      )
      if (response.ok) {
        yield put(runPricingCalculationSuccess(response.data))
      } else {
        const errorMessage = get(response, 'data.error.message', 'Calculation failed')
        yield put(runPricingCalculationFailure(errorMessage, response.status))
        yield put(snackbarMessage(errorMessage, 'error'))
      }
    } catch (e) {
      if (yield cancelled()) return
      console.error('Pricing calculator error:', e)
      yield put(runPricingCalculationFailure(e.message))
      yield put(snackbarMessage(e.message, 'error'))
      yield put(generalError(e.message))
    }
  }

  function* runCalculationCancellable(action) {
    yield race({
      task: call(runCalculation, action),
      cancelled: take([
        types.PRICING_CALCULATOR_RUN_CLEAR,
        types.PRICING_CALCULATOR_RESULT_RESET,
      ]),
    })
  }

  function* sendToSupport() {
    try {
      const lastExecution = yield select(getLastExecution)
      if (!lastExecution) {
        yield put(sendPricingCalculatorToSupportFailure('No calculation to send'))
        yield put(snackbarMessage('Run a calculation before sending to support', iconNames.error))
        return
      }

      const token = yield call(getOrRefreshToken)
      if (!token) {
        yield put(sendPricingCalculatorToSupportFailure('Calculator access token not available'))
        yield put(snackbarMessage('Unable to send to support', iconNames.error))
        return
      }

      const {calculator, version, request} = lastExecution
      const runResponse = yield call(
        api.pricingCalculator.run,
        token,
        calculator,
        version,
        {...request, debug: true}
      )
      const debugKey = get(runResponse, 'data.debugKey')
      if (!debugKey) {
        yield put(sendPricingCalculatorToSupportFailure('Debug payload not stored', runResponse.status))
        yield put(snackbarMessage('Unable to send to support', iconNames.error))
        return
      }

      const response = yield call(api.pricingCalculator.sendToSupport, debugKey, calculator, version)
      yield api.handleResponse(
        response,
        function* () {
          yield put(sendPricingCalculatorToSupportSuccess(debugKey))
          yield put(snackbarMessage('Sent to support', iconNames.tick))
        },
        function* (key, message, code) {
          yield put(sendPricingCalculatorToSupportFailure(message, code))
          yield put(snackbarMessage('Unable to send to support', iconNames.error))
        }
      )
    } catch (e) {
      console.error('Pricing calculator send to support error:', e)
      yield put(sendPricingCalculatorToSupportFailure(e.message))
      yield put(snackbarMessage('Unable to send to support', iconNames.error))
    }
  }

  // --- Inline editing prerequisite data preloading ---

  function extractPricingCalculatorItems(items) {
    const result = []
    if (!items) return result
    for (const item of items) {
      if (get(item, 'type.id') === OrderItemType.pricing_calculator && get(item, 'calculatorData')) {
        result.push(item)
      }
      const subItems = get(item, 'subItems')
      if (subItems) {
        result.push(...extractPricingCalculatorItems(subItems))
      }
    }
    return result
  }

  function getUniqueCalculatorVersions(calcItems) {
    const seen = new Set()
    const result = []
    for (const item of calcItems) {
      const name = get(item, 'calculatorData.name')
      const version = get(item, 'calculatorData.version')
      if (name && version) {
        const key = `${name}/${version}`
        if (!seen.has(key)) {
          seen.add(key)
          result.push({name, version, key})
        }
      }
    }
    return result
  }

  function* fetchFieldMappingsIfNeeded(name, version, key) {
    const existing = yield select(getFieldMappings)
    if (existing[key]) return

    const token = yield call(getOrRefreshToken)
    if (!token) return

    const response = yield call(api.pricingCalculator.getFields, token, name, version)
    if (response.ok) {
      yield put(setFieldMappings(key, response.data))
    } else {
      console.error(`Failed to load field mappings for ${key}`, get(response, 'data.error.message'))
    }
  }

  function* preloadPricingCalculatorData({key, data}) {
    if (!isCalculatorBearingPivotKey(key)) return

    try {
      const items = data || []
      const calcItems = extractPricingCalculatorItems(items)
      if (calcItems.length === 0) return

      yield call(getOrRefreshToken)

      const calculatorVersions = getUniqueCalculatorVersions(calcItems)
      if (calculatorVersions.length > 0) {
        yield all(
          calculatorVersions.map(({name, version, key: calcKey}) =>
            call(fetchFieldMappingsIfNeeded, name, version, calcKey)
          )
        )
      }
    } catch (e) {
      console.error('Failed to preload pricing calculator data:', e)
    }
  }

  function* onItemAdded({entity, key}) {
    if (!isCalculatorBearingPivotKey(key)) return
    if (get(entity, 'type.id') !== OrderItemType.pricing_calculator) return
    if (!get(entity, 'calculatorData')) return

    try {
      const name = get(entity, 'calculatorData.name')
      const version = get(entity, 'calculatorData.version')
      if (!name || !version) return

      yield call(fetchFieldMappingsIfNeeded, name, version, `${name}/${version}`)
    } catch (e) {
      console.error('Failed to preload data for pricing calculator item:', e)
    }
  }

  function* loadPricingCalculators() {
    try {
      const token = yield call(getOrRefreshToken)
      if (!token) {
        yield put(loadAllPricingCalculatorsFailure('Calculator access token not available'))
        return
      }

      const response = yield call(api.pricingCalculator.listAll, token)
      if (response.ok) {
        yield put(loadAllPricingCalculatorsSuccess(response.data))
      } else {
        yield put(loadAllPricingCalculatorsFailure('Failed to load pricing calculator data', response.statusCode))
      }

    } catch (e) {
      yield put(loadAllPricingCalculatorsFailure(e.message, e.code))
    }
  }

  function* loadPricingCalculatorForm({calculatorName, calculatorVersion}) {
    try {
      // The settings scene renders the same resource inputs as the drawer, so
      // load resources here too (they don't need the calculator token).
      yield fork(loadResources)

      const token = yield call(getOrRefreshToken)
      if (!token) {
        yield put(loadPricingCalculatorFormFailure('Calculator access token not available'))
        return
      }

      const response = yield call(
        api.pricingCalculator.getForm,
        token,
        calculatorName,
        calculatorVersion,
        INPUT_LEVELS.MASTER
      )
      if (response.ok) {
        yield put(loadPricingCalculatorFormSuccess(response.data))
      } else {
        yield put(loadPricingCalculatorFormFailure('Failed to load pricing calculator form', response.statusCode))
      }

    } catch (e) {
      yield put(loadPricingCalculatorFormFailure(e.message, e.code))
    }
  }

  function* loadMasterCalculatorInstance({calculatorName, calculatorVersion}) {
    try {
      const fields = {
        default: true,
        inputs: {
          default: true,
          parts: {
            default: true,
            packs: true,
          },
        },
      }

      const response = yield call(api.pricingCalculator.getMasterInstance, calculatorName, calculatorVersion, fields)

      yield api.handleResponse(
        response,
        function* (key, data) {
          yield put(loadPricingCalculatorMasterSuccess(data.result[0] || null))
        },
        function* (key, message, code) {
          yield put(loadPricingCalculatorMasterFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(loadPricingCalculatorMasterFailure(e.message))
    }
  }

  function* updateMasterCalculatorInstance({data}) {
    try {
      const apiToCall = !!data.id
        ? api.pricingCalculator.updateMasterInstance
        : api.pricingCalculator.addMasterInstance

      const response = yield call(apiToCall, data)

      yield api.handleResponse(
        response,
        function* (key, data) {
          yield put(updatePricingCalculatorMasterSuccess(data.result[0] || null))
        },
        function* (key, message, code) {
          yield put(updatePricingCalculatorMasterFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(updatePricingCalculatorMasterFailure(e.message))
    }
  }

  function* watcher() {
    yield takeLatest(types.QUOTING_QUOTE_SHOW_PRICING_CALCULATOR_DRAWER, onDrawerOpen)
    yield takeLatest(types.PRICING_CALCULATOR_RESOURCES_FORM_FETCH_ATTEMPT, fetchResourcesForm)
    yield fork(watchResourceTypes)
    yield takeLatest([
      types.PRICING_CALCULATOR_RESOURCE_TYPE_SELECT,
      types.PRICING_CALCULATOR_RESOURCE_INCLUDE_DORMANT_TOGGLE,
      types.PRICING_CALCULATOR_RESOURCE_ADD_SUCCESS,
      types.PRICING_CALCULATOR_RESOURCE_UPDATE_SUCCESS,
    ], reloadSelectedResources)
    yield takeLatest(types.PRICING_CALCULATOR_RESOURCE_ADD_ATTEMPT, addResource)
    yield takeLatest(types.PRICING_CALCULATOR_RESOURCE_UPDATE_ATTEMPT, updateResource)
    yield takeLatest(types.PRICING_CALCULATOR_RESOURCES_DORMANT_SET_ATTEMPT, setResourcesDormant)
    yield takeLatest(types.PRICING_CALCULATOR_RUN_ATTEMPT, runCalculationCancellable)
    yield takeLatest(types.PRICING_CALCULATOR_SEND_TO_SUPPORT_ATTEMPT, sendToSupport)
    yield takeLatest(types.PIVOT_TABLE_LOAD_SUCCESS, preloadPricingCalculatorData)
    yield takeLatest(types.PIVOT_TABLE_UPDATE_DATA, preloadPricingCalculatorData)
    yield takeLatest(types.PIVOT_TABLE_ADD_ENTITY, onItemAdded)
    yield takeLatest(types.PIVOT_TABLE_ADD_ENTITY, sortModesOnAdd)
    yield takeLatest(types.PRICING_CALCULATORS_LOAD_ATTEMPT, loadPricingCalculators)
    yield takeLatest(types.PRICING_CALCULATOR_FORM_FETCH_ATTEMPT, loadPartPricingCalculatorForm)
    yield takeLatest(types.PRICING_CALCULATOR_FORM_LOAD_ATTEMPT, loadPricingCalculatorForm)
    yield takeLatest(types.PRICING_CALCULATOR_MASTER_LOAD_ATTEMPT, loadMasterCalculatorInstance)
    yield takeLatest(types.PRICING_CALCULATOR_MASTER_UPDATE_ATTEMPT, updateMasterCalculatorInstance)
  }

  return {
    watcher,
  }
}
